import {useEffect, useMemo} from 'react';
import {getLocale} from 'epic-common/src/locale';
import {useDispatch, useSelector} from 'react-redux';

import {getGamesState} from '../../selectors';
import gamesActions from '../../actions/fortnite-game/games.actions';
import {CodeNamesMap, DataItem, GamesData, GamesState} from '../../reducers/games.reducer';

import {RootState} from '../../index';
import {Option} from '../../types';

export enum GroupName {
    TOP_GAMES = 'Top Games',
    TOP_10_GAMES = 'Top 10 Games',
    TOP_10_TRENDING = 'Top 10 Trending',
    RECENTLY_PUBLISHED = 'Recently Published',
    POPULAR = 'Popular'
}

export interface UseGamesData {
    data: {
        selectOptions: Option[];
        recent10GamesOptions: Option[];
        top10GamesOptions: Option[];
        top10TrendingOptions: Option[];
        recentGamesOptions: Option[];
        popularGamesOptions: Option[];
        codeNamesMap: CodeNamesMap;
    };
    loading: boolean;
    error?: string;
}

type SecondaryTextGetter = (item: DataItem) => string | undefined;

const formatGames = (data: GamesData) => {
    const formatToOptions = (
        data: any[],
        groupName?: string,
        getSecondaryText?: SecondaryTextGetter
    ) =>
        data
            .map((option, index) =>
                option.merge({
                    label: option.get('title'),
                    secondaryText: getSecondaryText ? getSecondaryText(option) : undefined,
                    value: option.get('creativeIslandCode'),
                    groupName,
                    order: index
                })
            )
            .toJS();

    const formatter = new Intl.DateTimeFormat(getLocale(), {
        day: 'numeric',
        month: 'long'
    });

    const recentGamesOptions = formatToOptions(
        data.get('recent'),
        GroupName.RECENTLY_PUBLISHED,
        item => {
            const day = item.get('day');
            return day ? formatter.format(new Date(day)) : undefined;
        }
    );

    return {
        selectOptions: formatToOptions(data.get('all'), GroupName.TOP_GAMES, item =>
            item.get('creativeIslandCode')
        ),
        recentGamesOptions,
        recent10GamesOptions: recentGamesOptions.slice(0, 10),
        popularGamesOptions: formatToOptions(data.get('popular'), GroupName.POPULAR, item => {
            const day = item.get('day');
            return day ? formatter.format(new Date(day)) : undefined;
        }),
        top10GamesOptions: formatToOptions(
            data.get('top10'),
            GroupName.TOP_10_GAMES,
            item => item.get('ccu') || 0
        ),
        top10TrendingOptions: formatToOptions(
            data.get('top10Trending'),
            GroupName.TOP_10_TRENDING,
            item => item.get('trend') || 0
        ),
        codeNamesMap: data.get('codeNamesMap').toJS()
    };
};

const useGamesData = (): UseGamesData => {
    const dispatch = useDispatch();

    const gamesState = useSelector<RootState, GamesState>(getGamesState);

    useEffect(() => {
        dispatch(gamesActions.getGames({}));
    }, []);

    const formattedData = useMemo(() => formatGames(gamesState.get('data')), [gamesState]);

    return {
        data: formattedData,
        loading: gamesState.get('loading'),
        error: gamesState.get('error')
    };
};

export default useGamesData;
